import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Table, InputGroup, FormControl, Button, Spinner } from 'react-bootstrap';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
  useResizeColumns,
  useFlexLayout,
  useFilters,
} from 'react-table';

const Container = tw.div`container bg-white rounded border border-gray-200 shadow-lg p-4`;

const Header = tw.div`flex justify-between items-center mb-4`;
const Title = tw.h2`text-gray-800 font-semibold text-xl`;
const TableWrapper = tw.div`mt-4 overflow-x-auto`;

const StyledTable = styled(Table)`
  ${tw`min-w-full`}
  font-size: 11px;
  font-family: 'Arial', sans-serif;
  border: 1px solid #ccc;
  border-collapse: collapse;

  thead {
    ${tw`bg-gray-200`}
    
    th {
      ${tw`border border-gray-300 text-center`}
      background-color: #f0f0f0;
      font-weight: bold;
      white-space: normal;
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 8px;
      position: relative;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
  }

  tbody {
    tr {
      ${tw`border-b border-gray-200`}
      
      &:hover {
        ${tw`bg-gray-100`}
      }
    }

    td {
      ${tw`border border-gray-300 py-2 px-4`}
      padding: 8px;
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    .align-right {
      text-align: right;
    }
  }
`;

const FilterInput = styled(FormControl)`
  ${tw`border border-gray-300`}
  height: 30px;
  font-size: 14px;
`;

const RefreshButton = styled(Button)`
  ${tw`ml-2`}
`;

const ResizableHeader = ({ className, column }) => {
  const { getHeaderProps, getSortByToggleProps, isSorted, isSortedDesc, canResize } = column;

  return (
    <th className={className} {...getHeaderProps()}>
      <div style={{ position: 'relative', width: '100%' }}>
        <span>{column.render('Header')}</span>
        {column.canSort && (
          <button
            className="sort-button"
            {...getSortByToggleProps()}
            style={{ padding: '0', marginLeft: '5px', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {isSorted ? (isSortedDesc ? '↓' : '↑') : '↕'}
          </button>
        )}
        {canResize && (
          <div
            {...column.getResizerProps()}
            className="resizer"
            style={{ right: 0, position: 'absolute', top: 0, bottom: 0, cursor: 'col-resize', width: '10px' }}
          />
        )}
      </div>
    </th>
  );
};

const Commodity = () => {
  const [globalInstruments, setGlobalInstruments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [headers, setHeaders] = useState([]);

  const fetchData = () => {
    setLoading(true);
    axios
      .get('https://falcon-algo.onrender.com/api/summary/commodity')
      .then((response) => {
        const data = response.data?.dataList || [];
        const fetchedHeaders = response.data?.header || [];

        setHeaders(fetchedHeaders);

        const formattedData = data.map((item) =>
          fetchedHeaders.reduce((acc, header, index) => {
            acc[header.name] = item[index];
            return acc;
          }, {})
        );

        setGlobalInstruments(formattedData);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setError('Error fetching data. Please try again later.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const ChangePercentCell = ({ cell: { value } }) => (
    <span style={{ color: value >= 0 ? 'green' : 'red' }}>{value.toFixed(2)}%</span>
  );

  const columns = useMemo(
    () =>
      headers.map((header) => ({
        Header: header.name,
        accessor: header.name.toLowerCase(),
        Cell: ({ cell }) => {
          // Check if value is numeric
          const numericValue = parseFloat(cell.value);
          const isNumeric = !isNaN(numericValue) && typeof numericValue === 'number';

          return (
            <div className={isNumeric ? 'align-right' : ''}>
              {isNumeric ? numericValue.toFixed(2) : cell.value}
            </div>
          );
        },
      })),
    [headers]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: globalInstruments,
      initialState: { pageIndex: 0, pageSize: 10, globalFilter: '' },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useResizeColumns,
    useFlexLayout
  );

  if (error) return <p>{error}</p>;

  return (
    <Container>
      <Header>
        <Title>Commodity</Title>
        <RefreshButton onClick={fetchData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </RefreshButton>
      </Header>
      <InputGroup className="mb-3">
        <FilterInput
          value={state.globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </InputGroup>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
          </Spinner>
            <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <TableWrapper>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <ResizableHeader
                      key={column.id}
                      column={column}
                      className="text-center"
                    />
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className={cell.column.align === 'right' ? 'align-right' : ''}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </TableWrapper>
      )}
    </Container>
  );
};

export default Commodity;
